/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

import * as React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { installGlobalErrorHandlers, logger } from "../core/logging";
import { APP_VERSION, IS_DEV } from "../core/config/env";

/* global document, Office, window */

installGlobalErrorHandlers();

logger.info("Task pane loading", {
  version: APP_VERSION,
  mode: IS_DEV ? "development" : "production",
  url: typeof window !== "undefined" ? window.location.href : undefined,
});

function renderFallback(message: string): void {
  const container = document.getElementById("app-root");
  if (!container) {
    return;
  }

  const root = createRoot(container);
  root.render(
    <div
      style={{
        fontFamily: "Segoe UI, Arial, sans-serif",
        padding: "24px",
        lineHeight: 1.5,
        color: "#1f2937",
      }}
    >
      <h2 style={{ marginTop: 0 }}>Word Add-in Page</h2>
      <p style={{ marginBottom: "8px" }}>
        This page is part of a Microsoft Word add-in and is intended to run inside Word.
      </p>
      <p style={{ marginTop: 0 }}>{message}</p>
    </div>
  );
}

function renderWordApp(): void {
  const container = document.getElementById("app-root");
  if (!container) {
    return;
  }

  const root = createRoot(container);
  root.render(<App />);
}

if (typeof Office === "undefined" || !Office.onReady) {
  logger.warn("Office.js not present — rendering fallback page");
  renderFallback("Open Word and launch the add-in from the ribbon to use the tools.");
} else {
  Office.onReady((info) => {
    logger.info("Office host ready", {
      host: String(info.host),
      platform: String(info.platform),
    });

    if (info.host === Office.HostType.Word) {
      renderWordApp();
      logger.info("Add-in ready");
      return;
    }

    logger.warn("Add-in opened outside Word", { host: String(info.host) });
    renderFallback("Please open this add-in from Microsoft Word.");
  }).catch((err: unknown) => {
    logger.error("Office.onReady failed", { error: err });
    renderFallback("Unable to connect to Office host context.");
  });
}

// Suppress "Script error." noise that originates from cross-origin Office.js
// (appsforoffice.microsoft.com).  Browsers intentionally strip error details
// for cross-origin scripts, so these messages are never actionable here.
window.addEventListener("error", (event) => {
  if (event.message === "Script error.") {
    event.stopImmediatePropagation();
    event.preventDefault();
  }
});

// Some runtimes report a generic 'Script error.' for cross-origin failures
// (Office.js). Add an additional onerror handler that returns `true` to
// suppress the browser's default logging for that specific message while
// allowing other errors to surface normally.
// This helps avoid noisy, un-actionable runtime error reports.
window.onerror = function (message) {
  try {
    if (message === 'Script error.') {
      return true; // suppress
    }
  } catch {
    // ignore
  }
  // returning false lets the default handler run for actionable errors
  return false;
};

// Also capture unhandled promise rejections to surface useful details and
// suppress only the generic cross-origin script error if it appears.
window.addEventListener('unhandledrejection', (ev) => {
  const reason = ev?.reason;
  if (typeof reason === 'string' && reason === 'Script error.') {
    ev.preventDefault();
  }
});
